import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';

import { Sitecore, SitecoreMyBetsConfig } from '@frontend/sports/common/client-config-data-access';
import { ActiveModal } from '@frontend/sports/modal/feature';
import { TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';
import { IntlService } from '@frontend/vanilla/core';
import { ResponsiveFooterContent } from '@frontend/vanilla/features/footer';
import { Subscription } from 'rxjs';

import { EpcotModule } from '../../../common/epcot-config.service';
import {
    IFilterConstraint,
    MyBetsDateRangeFilterContstraint,
    MyBetsFilterConstraint,
    MyBetsStateFilterContstraint,
} from '../../models/my-bets-filter-constraints';
import { IMyBetsListRequest } from '../../services/my-bets-api.service';

export interface IMyBetsSettledFiltersProps {
    stateModel: string;
    fromDate?: string;
    toDate?: string;
}

interface ITranslations {
    all: string;
    won: string;
    lost: string;
    cancelled: string;
    title: string;
    reset_button_title: string;
    bet_result_title: string;
    time_range_title: string;
    results_button_title: string;
    interval_adapted_message: string;
    start_date_placeholder: string;
    end_date_placeholder: string;
    contact_us: string;
}

@Component({
    selector: 'ms-my-bets-settled-filters',
    templateUrl: './my-bets-settled-filters.html',
    standalone: false,
})
export class MyBetsSettledFiltersComponent implements IMyBetsSettledFiltersProps, AfterViewInit, OnInit, OnDestroy {
    private filterConstraint: MyBetsFilterConstraint = new MyBetsFilterConstraint();
    private _stateMode: string;
    private _fromDate: string;
    private _toDate: string;
    private defaultFromDate: string;
    private defaultToDate: string;
    private maxDateInterval = 89;
    private initialTimePeriodInterval = 2592000000; // 30 days in miliseconds
    private now = new Date();
    private nowMinus30days = new Date(this.now.valueOf() - this.initialTimePeriodInterval);
    private columnSubscription?: Subscription;

    helpAndContact: string;
    module = EpcotModule.MyBetsBetSlip;
    isStateModelDirty = false;
    showIntervalAutoChangedInfo = false;
    translations: ITranslations;

    @Output() requestResults = new EventEmitter<IFilterConstraint<IMyBetsListRequest>[]>();
    @Input() isRangeSet = true;

    get stateModel(): string {
        return this._stateMode;
    }

    @Input()
    set stateModel(value: string) {
        this._stateMode = value;
        this.updateTitleStyle();
    }

    get fromDate(): string {
        return this._fromDate;
    }

    @Input()
    set fromDate(value: string | undefined) {
        this._fromDate = value ?? '';
        this.showIntervalAutoChangedInfo = false;
        if (value) {
            const date = new Date(value);
            if (this.toDate) {
                // Set from Date and there is todate.
                const toDateDate = new Date(this.toDate);
                if (date.valueOf() > toDateDate.valueOf()) {
                    // user sets from date after to date.
                    this.toDate = this.getMaxDate(date, 0);
                } else if (this.getDateDifference(date, toDateDate) > this.maxDateInterval) {
                    // user sets big range.
                    this.toDate = this.getMaxDate(date);
                    this.showIntervalAutoChangedInfo = true;
                }
            } else {
                // if no to date set it to max.
                this.toDate = this.getMaxDate(date);
            }
        }
        this.updateTitleStyle();
    }

    get toDate(): string {
        return this._toDate;
    }

    @Input()
    set toDate(value: string | undefined) {
        this._toDate = value ?? '';
        this.showIntervalAutoChangedInfo = false;
        if (value) {
            const date = new Date(value);
            if (this.fromDate) {
                // if from date.
                const fromDateDate = new Date(this.fromDate);
                if (fromDateDate.valueOf() > date.valueOf()) {
                    // user sets to date before from date.
                    this.fromDate = this.getMinDate(date, 0);
                } else if (this.getDateDifference(fromDateDate, date) > this.maxDateInterval) {
                    // if user sets big range
                    this.fromDate = this.getMinDate(date);
                    this.showIntervalAutoChangedInfo = true;
                }
            } else {
                // No from date, set it to - range
                this.fromDate = this.getMinDate(date);
            }
        }
        this.updateTitleStyle();
    }

    rangeConstraints = {
        minFromDate: '',
        maxFromDate: '',
        minToDate: '',
        maxToDate: '',
    };

    constructor(
        private intlService: IntlService,
        sitecoreMyBets: SitecoreMyBetsConfig,
        sitecore: Sitecore,
        private activeModal: ActiveModal,
        private footerConfig: ResponsiveFooterContent,
        private trackingService: TrackingService,
    ) {
        this.setInitialDatesAndRange();

        this.translations = {
            all: sitecoreMyBets.myBets.ALL,
            won: sitecoreMyBets.myBets.WON,
            lost: sitecoreMyBets.myBets.LOST,
            cancelled: sitecoreMyBets.myBets.CANCELED,
            title: sitecore.myBets_FilterView.Title,
            reset_button_title: sitecore.myBets_FilterView.ResetButtonTitle,
            bet_result_title: sitecore.myBets_FilterView.BetResultTitle,
            time_range_title: sitecore.myBets_FilterView.TimeRangeTitle,
            results_button_title: sitecore.myBets_FilterView.ResultsButtonTitle,
            interval_adapted_message: sitecore.myBets_FilterView.IntervalAdaptedMessage,
            end_date_placeholder: sitecore.myBets_FilterView.EndDatePlaceHolder,
            start_date_placeholder: sitecore.myBets_FilterView.StartDatePlaceHolder,
            contact_us: sitecore.myBets_FilterView.ContactUs,
        };
    }

    ngOnInit(): void {
        if (!this.isRangeSet) {
            this.setInitialDatesAndRange();
        }
        this.populateFilterConstraint();
        this.helpAndContact = this.footerConfig.helpButton.url;
    }

    ngAfterViewInit(): void {
        this.updateTitleStyle();
    }

    ngOnDestroy(): void {
        if (this.columnSubscription) {
            this.columnSubscription.unsubscribe();
        }
    }

    handleResetButtonClick(): void {
        this.stateModel = '';
        this.fromDate = this.dateToInputValue(this.nowMinus30days);
        this.toDate = this.getMaxDate(this.nowMinus30days);
    }

    onFilter(): void {
        this.populateFilterConstraint();
        const tracking = this.setTracking();

        if (tracking !== '') {
            this.trackingService.track(trackingConstants.EVENT_PAGE_VIEW, { [trackingConstants.PAGE_NAME]: tracking });
        }

        this.activeModal.close({ constaints: this.filterConstraint.constraints });
        this.requestResults.emit(this.filterConstraint.constraints);
    }

    private updateTitleStyle(): void {
        this.isStateModelDirty = this.isModelDirty();
    }

    private isModelDirty(): boolean {
        this.defaultFromDate = this.dateToInputValue(this.nowMinus30days);
        this.defaultToDate = this.getMaxDate(this.nowMinus30days);

        return !!this.stateModel || this.fromDate !== this.defaultFromDate || this.toDate !== this.defaultToDate;
    }

    private setInitialDatesAndRange(): void {
        this.rangeConstraints.maxFromDate = this.rangeConstraints.maxToDate = this.getMaxDate(this.nowMinus30days);
        this.fromDate = this.dateToInputValue(this.nowMinus30days);
        this.toDate = this.getMaxDate(this.nowMinus30days);
    }

    private getMinDate(date: Date, days: number = this.maxDateInterval): string {
        const minFromDate = new Date(date);
        minFromDate.setDate(minFromDate.getDate() - days);

        return this.dateToInputValue(minFromDate);
    }

    private getMaxDate(date: Date, days: number = this.maxDateInterval): string {
        const maxFromDate = new Date(date);
        maxFromDate.setDate(maxFromDate.getDate() + days);
        const now = new Date();

        return maxFromDate.valueOf() < now.valueOf() ? this.dateToInputValue(maxFromDate) : this.dateToInputValue(now);
    }

    private getDateDifference(fromDate: Date, toDate: Date): number {
        const val = toDate.valueOf() - fromDate.valueOf();

        return Math.ceil(val / 1000 / 60 / 60 / 24);
    }

    private getTitle(value: Date): string | null {
        return this.intlService.formatDate(value, 'shortDate');
    }

    setState(state: string): void {
        this.stateModel = state;
    }

    private getStateTitle(state: string): string {
        switch (state) {
            case 'Won':
                return this.translations.won;
            case 'Lost':
                return this.translations.lost;
            case 'Other':
                return this.translations.cancelled;
            default:
                return this.translations.all;
        }
    }

    private populateFilterConstraint(): void {
        this.filterConstraint.clean();
        if (this.stateModel) {
            this.filterConstraint.addConstraint(new MyBetsStateFilterContstraint(this.getStateTitle(this.stateModel), this.stateModel));
        }
        const now = new Date();
        if ((this.fromDate || this.toDate) && (this.fromDate !== this.defaultFromDate || this.toDate !== this.defaultToDate)) {
            const fromDateLocal = this.fromDate ? this.localDayStartFromUtc(this.fromDate) : this.localDayStartFromUtc(this.getMinDate(now));
            const toDateLocal = this.toDate ? this.localDayStartFromUtc(this.toDate) : this.localDayStartFromUtc(this.getMaxDate(fromDateLocal));
            const title = `${this.getTitle(fromDateLocal)} - ${this.getTitle(toDateLocal)}`;
            this.filterConstraint.addConstraint(new MyBetsDateRangeFilterContstraint(title, fromDateLocal, toDateLocal));
        }
    }

    // returns the local start of the selected day
    // eg. '2021-06-20' => 2021-06-20 00:00:00 GMT-1000
    private localDayStartFromUtc(dateShortStr: string): Date {
        const date = new Date(dateShortStr);

        return new Date(date.getTime() + date.getTimezoneOffset() * 60000);
    }

    private setTracking(): string {
        let tracking = 'mybets_Filter';
        if (this.stateModel) {
            tracking += '-' + this.stateModel.toLowerCase();
        }
        if ((this.fromDate || this.toDate) && (this.fromDate !== this.defaultFromDate || this.toDate !== this.defaultToDate)) {
            tracking += '-date';
        }

        return tracking;
    }

    private dateToInputValue(date: Date): string {
        return this.intlService.formatDate(date, 'yyyy-MM-dd');
    }
}
